import Icon from '@/components/snippets/icon/icon'
import clsx from 'clsx'
import { useState } from 'preact/hooks'
import { globalState } from '@/components/helpers/utils'

const getStockStatus = (variant, stockLevels) => {
  if (!variant) return { inStock: false, text: '' }

  const quantity = variant.inventory_quantity
  const zeroStockLevel = stockLevels.find((stock) => stock.max_quantity === 0)

  if (quantity <= 0 && zeroStockLevel) {
    return {
      inStock: false,
      text: zeroStockLevel.message,
      warning: true,
      stockLevel: quantity,
      textColour: zeroStockLevel.message_colour
    }
  }

  if (quantity <= 0) {
    return { inStock: false, text: 'Out of stock', warning: true, stockLevel: quantity, textColour: '#b3b3b3' }
  }

  for (const stock of stockLevels) {
    if (quantity <= stock.max_quantity) {
      return {
        inStock: true,
        text: stock.message,
        warning: true,
        stockLevel: quantity,
        textColour: stock.message_colour
      }
    }
  }

  return {
    inStock: true,
    text: '',
    warning: false,
    stockLevel: quantity,
    textColour: ''
  }
}

export function ResultQuickAdd({ result }) {
  const variants = Array.isArray(result?.variants) ? result.variants : []
  const hasSingleVariant = variants.length <= 1
  const singleVariant = hasSingleVariant ? variants[0] : null
  const stockLevels = globalState?.metaobjects?.stock_indicators?.sort((a, b) => b.max_quantity - a.max_quantity) || []
  const dropdownId = `quick-add-size-${result?.uid ?? result?.id ?? result?.handle}`

  const [selectedSize, setSelectedSize] = useState('')
  const [loading, setLoading] = useState(false)

  const safeVariants = variants.filter((v) => v && v.id != null)

  const getDisplaySize = (variant) => {
    if (!variant) return 'N/A'
    return (
      (variant.option2 && String(variant.option2).trim()) ||
      (variant.option1 && String(variant.option1).trim()) ||
      (variant.title && String(variant.title).trim()) ||
      String(variant.id) ||
      'N/A'
    )
  }

  const getStatusForSize = (size) => {
    const variant = safeVariants.find((v) => {
      const s = v.option2 || v.option1 || v.title || String(v.id)
      return s === size
    })
    return getStockStatus(variant, stockLevels)
  }

  const closeDropdown = () => {
    const modal = document.querySelector(`x-modal[data-id="${dropdownId}"]`)
    if (modal && typeof modal.handleHide === 'function') modal.handleHide()
  }

  const handleSizeSelect = (variant) => {
    if (!variant || !variant.id) return
    const variantSize = variant.option2 || variant.option1 || variant.title || String(variant.id)
    setSelectedSize(variantSize)
    closeDropdown()
  }

  const handleAddToCart = async () => {
    if (!selectedSize || !window.Cart) return
    const sizeToMatch = selectedSize.split('/')[0].trim()
    const targetVariant = variants.find(
      (v) => v.option2 === sizeToMatch || v.option1 === sizeToMatch || v.title === selectedSize
    )
    if (!targetVariant) return
    const variantId = typeof targetVariant.id === 'string' ? parseInt(targetVariant.id, 10) : targetVariant.id
    setLoading(true)
    try {
      await window.Cart.addLineItems({ id: variantId, quantity: 1 })
      setSelectedSize('')
    } catch (e) {
      console.error('Error adding item to cart:', e)
    } finally {
      setLoading(false)
    }
  }

  const selectedVariant = selectedSize
    ? safeVariants.find((v) => (v.option2 || v.option1 || v.title || String(v.id)) === selectedSize)
    : null
  const selectedStock = selectedVariant ? getStatusForSize(selectedSize) : null
  const canAddToCart = selectedVariant && selectedStock?.inStock !== false

  // Single variant: one ATC button
  if (hasSingleVariant && singleVariant) {
    const stockStatus = getStockStatus(singleVariant, stockLevels)
    const isDisabled = !stockStatus.inStock

    return (
      <div className="flex w-full">
        <button
          type="button"
          className={clsx('w-full btn btn-primary btn-s', isDisabled ? 'disabled btn-border' : 'btn-fill')}
          onClick={() => !isDisabled && window.Cart?.addLineItems({ id: singleVariant.id, quantity: 1 })}
          disabled={isDisabled}
          aria-label={isDisabled ? 'Out of Stock' : 'Add To Bag'}
        >
          <span>{isDisabled ? 'Out of Stock' : 'Add To Bag'}</span>
        </button>
      </div>
    )
  }

  const buttonClass = 'btn btn-s w-full'

  return (
    <div className="flex w-full grid-cols-2 flex-col items-center gap-0.5 lg:grid">
      <x-modal data-id={dropdownId} className="group/size-dropdown size-full" data-hide-on-blur="true">
        <button
          type="button"
          className="flex h-[36px] w-full items-center justify-between gap-x-0.5 rounded-8 border border-neutral-medium bg-neutral-white px-1 py-0.5 text-left transition-all hover:border-primary group-data-visible/size-dropdown:border-primary"
          data-modal-parent={dropdownId}
          data-modal-role="toggle"
          aria-haspopup="listbox"
          aria-label="Size"
        >
          <span
            size-selector-label
            className={clsx(
              'font-heading paragraph-b font-medium uppercase',
              !selectedVariant && 'text-neutral-secondary'
            )}
          >
            {selectedVariant ? getDisplaySize(selectedVariant) : 'Size'}
          </span>
          <span className="h-[8px] w-[14px] transition-all group-data-visible/size-dropdown:-rotate-180 children:size-full">
            <Icon id="chevron-down" />
          </span>
        </button>
        <div
          className="flex flex-col absolute inset-tl size-full bg-brand-white opacity-0 opacity-0 group-data-visible/size-dropdown:opacity-100 pointer-events-none group-data-visible/size-dropdown:pointer-events-auto invisible group-data-visible/size-dropdown:visible overflow-y-scroll no-scrollbar z-10 transition-all"
          role="listbox"
          aria-label="Size"
        >
          <div class="flex sticky inset-tl w-full bg-brand-white px-1 py-0.75 border-b border-neutral-medium z-2">
            <span class="paragraph-s font-medium">
              Select Size
            </span>
          </div>
          {safeVariants.map((v, index) => {
            const variantSize = v.option2 || v.option1 || v.title || String(v.id)
            const status = getStatusForSize(variantSize) || { inStock: true, text: '' }
            const canSelect = status.inStock !== false
            const displaySize = getDisplaySize(v)
            const inputId = `${dropdownId}-Size-${variantSize}-${v.id}-${index}`
            const isSelected = selectedSize === variantSize
            return (
              <div
                key={v.id}
                role="button"
                aria-selected={isSelected}
                tabIndex={canSelect ? 0 : -1}
                className={clsx(
                  'flex relative cursor-pointer px-1 py-0.75 hover:bg-neutral-white transition-all',
                  index !== safeVariants.length - 1 && 'border-b border-neutral-medium',
                  canSelect ? '' : 'opacity-50'
                )}
                onClick={() => {
                  if (canSelect) handleSizeSelect(v)
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && canSelect) {
                    e.preventDefault()
                    handleSizeSelect(v)
                  }
                }}
                aria-label={displaySize}
                aria-disabled={!canSelect}
              >
                <input
                  id={inputId}
                  name="Size"
                  type="radio"
                  value={variantSize}
                  className="absolute inset-0 size-full opacity-0 cursor-pointer"
                  aria-label={displaySize}
                  checked={isSelected}
                  onChange={() => {}}
                />
                <label
                  htmlFor={inputId}
                  className={clsx(
                    'paragraph-s flex w-full cursor-pointer items-center justify-between gap-x-0.5 transition-all',
                    !canSelect && 'opacity-50'
                  )}
                >
                  <span>{displaySize}</span>
                  {status.text && (
                    <span
                      className="paragraph-s font-medium !leading-none"
                      style={status.textColour ? { color: status.textColour } : undefined}
                    >
                      {status.text}
                    </span>
                  )}
                </label>
              </div>
            )
          })}
          <button
            type="button"
            className="sr-only"
            data-modal-parent={dropdownId}
            data-modal-role="close"
            aria-label="Close"
          >
            Close
          </button>
        </div>
      </x-modal>
      <button
        type="button"
        className={clsx(
          buttonClass,
          !selectedSize && 'pointer-events-none btn-fill btn-light btn-light-alt',
          selectedSize && canAddToCart && 'btn-fill btn-primary',
          selectedSize && !canAddToCart && 'disabled btn-border btn-primary',
          loading && 'disabled'
        )}
        onClick={() => canAddToCart && handleAddToCart()}
        disabled={!canAddToCart || loading}
        aria-label={!selectedSize ? 'Select' : canAddToCart ? 'Add To Bag' : 'Out of Stock'}
      >
        <span>{!selectedSize ? 'Select' : canAddToCart ? 'Add To Bag' : 'Out of Stock'}</span>
      </button>
    </div>
  )
}
